import type { LucideIcon } from 'lucide-react';
import { Bell, FileSignature, Globe, Network, ScrollText, ShieldCheck, UserCheck } from 'lucide-react';
import { EBENEN } from '../../config/ebenen';
import { navSymbol } from '../../pages/einstellungen/navSymbole';
import {
  BESITZ_ANKER, FIRMEN_ANSICHTEN, FIRMEN_SICHERHEIT_ANKER, ORGANISATION_PFAD, SUCHE, bereichErlaubt, bereichsTitel, firmenAnsichtAdresse,
  navAdresse, organisationsAdresse, suchForm, sucheTreffer,
} from '../../pages/einstellungen/seiten';
import type { Bereich, FirmenKontext } from '../../pages/einstellungen/seiten';
import type { CustomerKind } from '../api';
import { BENACHRICHTIGUNGS_VORGABEN_TEXTE, RECHERCHE_VORGABEN_TEXTE } from './vorgabenTexte';
import { FIRMENDATEN_STICHWOERTER } from './rechnungsdatenTexte';

// Profil-Nachbau F-11 (BAU-FIRMA.md 1.2 bis 1.4): Leiste der Seite „Einstellungen der Firma“. Heutige Einträge nehmen Recht,
// Titel und Adresse aus FIRMEN_BEREICHE (seiten.ts, nur gelesen); neue Seiten tragen ihre Zeile hier selbst ein, wer die Seite
// baut (Recherche, Benachrichtigungen, Teams, Verträge & AVV, Nutzungsrichtlinie).
// Gruppen und Reihenfolge wörtlich nach F-11; nie in der Leiste: „Team-Nutzung“ (usage, geht im KI-Cockpit auf),
// „Einladungen“ (invitations, Abschnitt in „Mitglieder“) und „Begriffe“ (branding, Abschnitt in „Firmendaten“). Die Leiste
// blendet nur aus; jeder Weg des Servers prüft seine Rechte selbst. Symbole wie in navSymbole.ts für dieselbe Sache, neu nur
// UserCheck und ShieldCheck für die zwei Ansichten von „Firmendaten“, die früher als Unter-Einträge ohne Symbol standen, und Globe
// für die neue Seite „Recherche“ (PF-704); „Benachrichtigungen“ mit Bell wie die persönliche Seite (navSymbole.ts); FileSignature
// und ScrollText für „Verträge & AVV“ und „Nutzungsrichtlinie“ (PF-786).

export type FirmenGruppenKey = 'firma' | 'personen' | 'geld' | 'vorgaben' | 'datenschutz';
/** Ein heutiger Bereich (mit Anker eine Ansicht von „Firmendaten“) oder eine neue Seite mit eigenem Recht, Titel, Adresse
 *  …/organisation/<slug> und eigenen Stichwörtern. PF-715: ein heutiger Bereich kann zu STICHWOERTER (seiten.ts) eigene
 *  Stichwörter dieser Leiste tragen, die nur mit stichwoerterRecht gelten (sie nennen Teile, die erst mit dem Recht dastehen).
 *  F-23 (PF-NB-firma-1): nurFuer nennt die Arten der Firma, die den Eintrag haben (ohne Angabe jede); stichwoerterNurFuer ebenso für
 *  die eigenen Stichwörter. */
export type FirmenEintragDef =
  | { art: 'bereich'; key: string; bereich: Bereich; anker?: string; titel?: string; symbol: LucideIcon; stichwoerter?: readonly string[]; stichwoerterRecht?: string;
    stichwoerterNurFuer?: readonly CustomerKind[]; nurFuer?: readonly CustomerKind[] }
  | { art: 'seite'; key: string; titel: string; slug: string; recht: string; symbol: LucideIcon; stichwoerter: string[]; nurFuer?: readonly CustomerKind[] };
/** stichwoerter: die eigenen Stichwörter eines heutigen Bereichs, nur wenn ihr Recht vorhanden ist (PF-715). */
export type FirmenEintrag = { key: string; titel: string; to: string; symbol: LucideIcon; aktiv: boolean; gefunden?: string; stichwoerter?: readonly string[] };
export type FirmenGruppe = { key: FirmenGruppenKey; titel: string; eintraege: FirmenEintrag[] };
export type FirmenLeiste = { key: FirmenGruppenKey; eintraege: FirmenEintragDef[] }[];

export const FIRMEN_TEXTE = {
  gruppen: { firma: 'Firma', personen: 'Personen und Zugang', geld: 'Nutzung und Geld', vorgaben: 'Vorgaben für alle',
    datenschutz: 'Datenschutz und Compliance' },
  // Leistenfuß (F-19 „unten, wo sinnvoll, der Bereichsname“), darunter der Firmenname aus dem Profil. Das Wort steht in
  // config/ebenen.ts; PF-107 stellt es auf „Einstellungen der Firma“ um.
  fuss: EBENEN.adminEinstellungen,
} as const;

// navSymbole.ts führt ein Symbol für jeden Bereich dieser Tabelle.
const heute = (bereich: Bereich): FirmenEintragDef => ({ art: 'bereich', key: bereich, bereich, symbol: navSymbol(bereich)! });
const ansicht = (anker: string) => FIRMEN_ANSICHTEN.find(a => a.anker === anker)!;
const firmendatenAnsicht = (anker: string, symbol: LucideIcon): FirmenEintragDef =>
  ({ art: 'bereich', key: anker, bereich: 'company', anker, titel: ansicht(anker).titel, symbol });
// F-23 (PF-NB-firma-1, SPEC-firma-b Abschnitt 2): die interne Organisation des Betriebs hat weder Vertrag und Rechnung noch Firmenstandard
// der Mails, AVV oder Nutzungsrichtlinie.
const NUR_KUNDEN: readonly CustomerKind[] = ['MANDANT', 'SOLO'];

export const FIRMEN_LEISTE: FirmenLeiste = [
  // PF-715 (F-12, AUFTRAG 4.6): „Firmendaten“ trägt seit der Seite nach Österreich die Rechnungs- und Vertragsangaben; die Suche
  // findet den Eintrag auch über deren Wörter (rechnungsdatenTexte.ts), ohne seiten.ts zu ändern – mit billing.read, denn ohne
  // stehen die Angaben dort nicht. F-23 (PF-NB-firma-1): ebenso nicht für die interne Organisation (PF-NB-firma-2, ohne Rechnungsteil).
  { key: 'firma', eintraege: [{ art: 'bereich', key: 'company', bereich: 'company', symbol: navSymbol('company')!, stichwoerter: FIRMENDATEN_STICHWOERTER,
    stichwoerterRecht: 'billing.read', stichwoerterNurFuer: NUR_KUNDEN }, firmendatenAnsicht(BESITZ_ANKER, UserCheck)] },
  { key: 'personen', eintraege: [
    heute('members'),
    // PF-805 (BAU-TEAMS.md 1.3, F-14): „Teams“ direkt nach „Mitglieder“; Teamseiten …/teams/<id> markieren den Eintrag als offen.
    // Titel wie der Seitenkopf (teams/teamTexte.ts); die Leiste bindet die Texte der Teams nicht ein (deren Geldbausteine).
    { art: 'seite', key: 'teams', titel: 'Teams', slug: 'teams', recht: 'members.read', symbol: Network,
      stichwoerter: ['Team', 'Teams', 'Abteilung', 'Gruppe', 'Team-Admin', 'KI-Champion', 'Teamkontingent'] },
    heute('roles'),
    firmendatenAnsicht(FIRMEN_SICHERHEIT_ANKER, ShieldCheck),
    // Frage F-F1 (Standard): „SSO“ als letzter Eintrag der Gruppe, Recht wie heute (bereichErlaubt).
    heute('sso'),
  ] },
  { key: 'geld', eintraege: [heute('cost'), heute('plan'), { ...heute('billing'), nurFuer: NUR_KUNDEN }] },
  { key: 'vorgaben', eintraege: [
    // PF-704 (BAU-FIRMA.md 1.6): Firmenvorgabe „Websuche erlaubt“ (F-09); Titel wie der Seitenkopf aus vorgabenTexte.ts.
    // PF-733 (F-20 Ergänzung 4): dazu „Glossar“, „Abkürzung“, „Langform“ für den Abschnitt „Glossar für die Suche“.
    { art: 'seite', key: 'research', titel: RECHERCHE_VORGABEN_TEXTE.titel, slug: 'recherche', recht: 'organization.read', symbol: Globe,
      stichwoerter: ['Websuche', 'Web', 'Google-Suche', 'Vorgaben', 'Glossar', 'Abkürzung', 'Langform'] },
    // PF-705 (BAU-FIRMA.md 1.7): Firmenstandard der Mails je Ereignis (F-10); Schlüssel notifications, nicht der persönliche Slug.
    { art: 'seite', key: 'notifications', titel: BENACHRICHTIGUNGS_VORGABEN_TEXTE.titel, slug: 'benachrichtigungen', recht: 'organization.read',
      symbol: Bell, stichwoerter: ['E-Mail', 'Mail', 'Firmenstandard', 'Vorgaben'], nurFuer: NUR_KUNDEN },
  ] },
  { key: 'datenschutz', eintraege: [
    // Profil-Nachbau F-16/F-17 (PF-786, BAU-RECHT.md 6): „Verträge & AVV“ und „Nutzungsrichtlinie“ vor „Änderungsprotokoll“; Titel
    // wie die Seitenköpfe (recht/avvTexte.ts, recht/richtlinieTexte.ts), Stichwörter aus BAU-RECHT 2.2 und 3.1.
    { art: 'seite', key: 'vertraege', titel: 'Verträge & AVV', slug: 'vertraege', recht: 'billing.read', symbol: FileSignature,
      stichwoerter: ['AVV', 'Auftragsverarbeitung', 'Unterauftragsverarbeiter', 'DSGVO', 'Datenschutz', 'Vertrag', 'Nachweise'], nurFuer: NUR_KUNDEN },
    { art: 'seite', key: 'nutzungsrichtlinie', titel: 'Nutzungsrichtlinie', slug: 'nutzungsrichtlinie', recht: 'policy.read', symbol: ScrollText,
      stichwoerter: ['Richtlinie', 'Regeln', 'Nutzungsregeln', 'KI-Richtlinie', 'Zustimmung', 'bestätigen', 'akzeptieren', 'Verhaltensregeln'], nurFuer: NUR_KUNDEN },
    heute('audit'),
  ] },
];

/** Pfad ohne Schrägstrich am Ende ist ORGANISATION_PFAD oder beginnt mit ORGANISATION_PFAD + '/'. */
export const istFirmenPfad = (pfad: string) => {
  const ohne = pfad.replace(/\/+$/, '');
  return ohne === ORGANISATION_PFAD || ohne.startsWith(`${ORGANISATION_PFAD}/`);
};

// F-23 (PF-NB-firma-1, E-3): ohne Art (Aufrufer außerhalb der Firmen-Seite, Tests) gilt jede Liste.
const fuerArt = (liste: readonly CustomerKind[] | undefined, art?: CustomerKind | null) => !liste || !art || liste.includes(art);
const sichtbar = (e: FirmenEintragDef, kontext: FirmenKontext, art?: CustomerKind | null) =>
  fuerArt(e.nurFuer, art) && (e.art === 'bereich' ? bereichErlaubt(e.bereich, kontext) : !!kontext?.permissions?.includes(e.recht));
// Mit Anker die Adresse der Ansicht von „Firmendaten“, wie früher die Unter-Einträge (…/firmendaten#besitz).
const adresse = (e: FirmenEintragDef) => e.art === 'seite' ? `${ORGANISATION_PFAD}/${e.slug}`
  : e.anker ? firmenAnsichtAdresse(ansicht(e.anker).ansicht) : organisationsAdresse(e.bereich);
const titel = (e: FirmenEintragDef) => e.art === 'seite' ? e.titel : e.titel ?? bereichsTitel(e.bereich);
// PF-723 (F-13): von den heutigen Bereichen hat nur „Mitglieder“ Unterseiten, die Mitgliedsseiten …/mitglieder/<id>.
const mitUnterseiten = (e: FirmenEintragDef) => e.art === 'seite' || (e.bereich === 'members' && !e.anker);

/** Sichtbare Gruppen in der Reihenfolge der Tabelle; leere Gruppen fallen weg; aktiv nach navAdresse(pfad, hash), eine neue Seite
 *  und „Mitglieder“ auch auf ihren Unterseiten (…/teams/<id>, …/mitglieder/<id>). F-23: mit art nur die Einträge dieser Art der
 *  Firma (nurFuer); die Suche (firmenSuche) findet dann nur, was die Leiste zeigt. */
export function firmenGruppen(kontext: FirmenKontext, pfad: string, hash: string, art?: CustomerKind | null): FirmenGruppe[] {
  return firmenGruppenAus(FIRMEN_LEISTE, kontext, pfad, hash, art);
}
/** firmenGruppen über eine andere Tabelle; nur für Tests (etwa mit einem Eintrag art: 'seite'). */
export function firmenGruppenAus(tabelle: FirmenLeiste, kontext: FirmenKontext, pfad: string, hash: string, art?: CustomerKind | null): FirmenGruppe[] {
  const offen = navAdresse(pfad, hash);
  return tabelle.map(gruppe => ({ key: gruppe.key, titel: FIRMEN_TEXTE.gruppen[gruppe.key],
    eintraege: gruppe.eintraege.filter(e => sichtbar(e, kontext, art)).map((e): FirmenEintrag => {
      const to = adresse(e);
      const eigene = e.art === 'bereich' && e.stichwoerter && (!e.stichwoerterRecht || !!kontext?.permissions?.includes(e.stichwoerterRecht))
        && fuerArt(e.stichwoerterNurFuer, art) ? { stichwoerter: e.stichwoerter } : {};
      return { key: e.key, titel: titel(e), to, symbol: e.symbol, aktiv: to === offen || (mitUnterseiten(e) && offen.startsWith(`${to}/`)), ...eigene };
    }) }))
    .filter(gruppe => gruppe.eintraege.length > 0);
}

/** Suche nur über die Einträge dieser Leiste (F-P1-Regel wie die Konto-Seite), Treffer wieder in ihren Gruppen. */
export function firmenSuche(gruppen: FirmenGruppe[], text: string): FirmenGruppe[] {
  return firmenSucheAus(FIRMEN_LEISTE, gruppen, text);
}
/** firmenSuche mit den Stichwörtern der neuen Seiten aus einer anderen Tabelle; nur für Tests.
 *  Heutige Einträge suchen mit sucheTreffer und STICHWOERTER (seiten.ts): ein Treffer über ein Stichwort trägt
 *  SUCHE.gefunden(wort) und dessen Anker im Ziel. Neue Seiten suchen in Titel und eigenen Stichwörtern nach derselben
 *  Vergleichsform (suchForm). Wie sucheInGruppen entfallen die Treffer über ein enthaltenes Stichwort, sobald ein Eintrag
 *  genau passt. Leerer Text liefert die Gruppen unverändert; Gruppen ohne Treffer fallen weg. PF-715: findet sucheTreffer einen
 *  heutigen Eintrag nicht, zählen seine eigenen Stichwörter (FirmenEintrag.stichwoerter, nur mit ihrem Recht) wie die einer neuen
 *  Seite. */
export function firmenSucheAus(tabelle: FirmenLeiste, gruppen: FirmenGruppe[], text: string): FirmenGruppe[] {
  if (!text.trim()) return gruppen;
  const q = suchForm(text.trim());
  const seiten = new Map(tabelle.flatMap(g => g.eintraege).flatMap(e => (e.art === 'seite' ? [[e.key, e.stichwoerter] as const] : [])));
  const treffer = (e: FirmenEintrag): { eintrag: FirmenEintrag; enthalten?: true } | null => {
    const stichwoerter = seiten.get(e.key);
    if (stichwoerter) {
      if (suchForm(e.titel).includes(q)) return { eintrag: e };
      const wort = stichwoerter.find(w => suchForm(w).includes(q));
      return wort ? { eintrag: { ...e, gefunden: SUCHE.gefunden(wort) } } : null;
    }
    const fund = sucheTreffer(e, text);
    if (!fund) {
      const wort = e.stichwoerter?.find(w => suchForm(w).includes(q));
      return wort ? { eintrag: { ...e, gefunden: SUCHE.gefunden(wort) } } : null;
    }
    const stichwort = fund.gefunden;
    return { eintrag: stichwort ? { ...e, gefunden: SUCHE.gefunden(stichwort.wort), to: stichwort.anker ? `${e.to}#${stichwort.anker}` : e.to } : e,
      enthalten: fund.enthalten };
  };
  const alle = gruppen.map(gruppe => gruppe.eintraege.map(treffer));
  const genau = alle.some(liste => liste.some(t => t && !t.enthalten));
  return gruppen.map((gruppe, g) => ({ ...gruppe, eintraege: alle[g].flatMap(t => (t && !(genau && t.enthalten) ? [t.eintrag] : [])) }))
    .filter(gruppe => gruppe.eintraege.length > 0);
}
